// Couverture régionale RÉELLE des lignes de LUXE, dérivée de la cache de voilages
// (clés idx:v2:region:nights:ym:company/...). Sert au « conseiller de cohérence » du
// funnel : prévenir AVANT la recherche quand une ligne de luxe ne ressort pas dans la
// région choisie (évite de brûler une recherche pour un écran quasi vide).
//
// ⚠️ Données = photo du 2026-06-02. RÉGÉNÉRER : `node gen-coverage-map.js` (lit la cache
// live). La région cache « europe » est un bucket EU large, distinct de europe_med/north/british.

// azamara EN HOLD (retirée) — CruiseDirect ne la vend pas. Cf. mémoire project_azamara_hold.
// crystal : ajoutée au picker 2026-06-07 mais PAS encore ici — pas de données de cache (le
// robot vient de commencer à la réchauffer). Régénérer (node gen-coverage-map.js) puis
// l'ajouter ici quand la cache aura des voilages, sinon le conseiller crée de fausses alertes.
export const LUXURY_LINES = ["oceania", "regent", "seabourn", "silversea", "cunard"];

export const LUX_LABEL = {
  oceania: "Oceania", regent: "Regent Seven Seas", seabourn: "Seabourn",
  silversea: "Silversea", cunard: "Cunard",
};

// Région funnel → libellé (fr/en) pour les messages.
export const REGION_LABEL = {
  carib_east:     { fr: "Caraïbes Est",       en: "Eastern Caribbean" },
  carib_west:     { fr: "Caraïbes Ouest",     en: "Western Caribbean" },
  carib_south:    { fr: "Caraïbes Sud",       en: "Southern Caribbean" },
  bermuda:        { fr: "Bermudes",           en: "Bermuda" },
  europe_med:     { fr: "Méditerranée",       en: "the Mediterranean" },
  europe_north:   { fr: "Europe du Nord",     en: "Northern Europe" },
  europe_british: { fr: "Îles britanniques",  en: "the British Isles" },
  alaska:         { fr: "Alaska",             en: "Alaska" },
  hawaii:         { fr: "Hawaï",              en: "Hawaii" },
  asia:           { fr: "Asie",               en: "Asia" },
  repositioning:  { fr: "Repositionnement",   en: "repositioning" },
};

// funnelValue → { régionCache: nb voilages } (mesuré dans la cache)
export const LUXURY_COVERAGE = {
  oceania:   { europe: 60, europe_med: 50, europe_british: 10, asia: 8, europe_north: 7, alaska: 5, carib_east: 3, carib_west: 1, carib_south: 1 },
  regent:    { europe: 38, europe_med: 32, europe_north: 10, europe_british: 4, carib_east: 4, carib_south: 3, carib_west: 2, asia: 2, bermuda: 1 },
  seabourn:  { europe: 40, europe_med: 38, carib_south: 7, carib_east: 7, europe_british: 2, asia: 2, europe_north: 1 },
  cunard:    { europe: 24, europe_med: 12, europe_british: 12, carib_south: 7, carib_east: 7, carib_west: 4, europe_north: 4 },
  silversea: {},  // absente de la cache (expédition/luxe peu couverte par notre source)
};

// « Où navigue surtout cette ligne » — précalculé, friendly, pour le message.
export const LUX_PRIMARY = {
  oceania:   { fr: "Europe et Méditerranée",                 en: "Europe and the Mediterranean" },
  regent:    { fr: "Europe et Méditerranée",                 en: "Europe and the Mediterranean" },
  seabourn:  { fr: "Europe et Méditerranée",                 en: "Europe and the Mediterranean" },
  cunard:    { fr: "Europe (Méditerranée, îles britanniques)", en: "Europe (Mediterranean, British Isles)" },
  silversea: { fr: "des itinéraires d'expédition",           en: "expedition itineraries" },
};

// Région funnel → régions cache qui la satisfont (europe large couvre les sous-zones EU).
const FUNNEL_REGION_TO_CACHE = {
  carib_east: ["carib_east"], carib_west: ["carib_west"], carib_south: ["carib_south"],
  bermuda: ["bermuda"], alaska: ["alaska"], hawaii: ["hawaii"], asia: ["asia"],
  repositioning: ["repositioning"],
  europe_med:     ["europe_med", "europe"],
  europe_north:   ["europe_north", "europe"],
  europe_british: ["europe_british", "europe"],
};

function coverCount(line, funnelRegion) {
  const cov = LUXURY_COVERAGE[line]; if (!cov) return 0;
  const cacheRegions = FUNNEL_REGION_TO_CACHE[funnelRegion] || [funnelRegion];
  return cacheRegions.reduce((s, r) => s + (cov[r] || 0), 0);
}

// Une ligne « couvre » une région funnel si elle a ≥1 voilage confirmé dans la cache.
export function lineCoversRegion(line, funnelRegion) {
  return coverCount(line, funnelRegion) > 0;
}

// Meilleure ligne de luxe alternative pour une région (la mieux couverte), seuil ≥3
// pour ne pas suggérer une ligne elle-même mince. Exclut les lignes déjà choisies.
export function bestLuxuryForRegion(funnelRegion, exclude = []) {
  let best = null, bestN = 0;
  for (const line of LUXURY_LINES) {
    if (exclude.includes(line)) continue;
    const n = coverCount(line, funnelRegion);
    if (n > bestN) { bestN = n; best = line; }
  }
  return bestN >= 3 ? best : null;
}

// CONSEILLER DE COHÉRENCE — Règle A : ligne(s) de luxe × région non couverte.
// Retourne null si OK, sinon un avis bilingue prêt à afficher dans le gate + un patch
// de correction (appliqué aux critères puis recherche relancée). CONSERVATEUR : ne se
// déclenche QUE si AUCUNE région choisie n'est couverte par AUCUNE ligne de luxe choisie
// (zéro voilage confirmé) — jamais sur du « mince » (le filet post-recherche gère ça).
export function assessLuxuryRegion(data) {
  const companies = data?.companies || [];
  const lux = companies.filter(c => LUXURY_LINES.includes(c));
  if (lux.length === 0) return null;                        // pas de ligne de luxe
  if (companies.some(c => !LUXURY_LINES.includes(c))) return null; // aussi des lignes grand public → on ne bloque pas
  const regions = (data?.regions || []).filter(r => r && r !== "any" && REGION_LABEL[r]);
  if (regions.length === 0) return null;                    // pas de région précise

  const covered = regions.some(rg => lux.some(l => lineCoversRegion(l, rg)));
  if (covered) return null;                                 // au moins une combinaison existe → pas d'alerte

  // Aucune combinaison ligne-luxe × région choisie n'a de voilage confirmé.
  const lineNames = lux.map(l => LUX_LABEL[l]).join(", ");
  const regFr = regions.map(r => REGION_LABEL[r].fr).join(", ");
  const regEn = regions.map(r => REGION_LABEL[r].en).join(", ");
  const primFr = LUX_PRIMARY[lux[0]]?.fr || "d'autres régions";
  const primEn = LUX_PRIMARY[lux[0]]?.en || "other regions";

  // Correction proposée : une autre ligne de luxe qui navigue VRAIMENT dans la 1re région,
  // sinon on retire le filtre compagnie (toutes les compagnies dans la région voulue).
  const alt = bestLuxuryForRegion(regions[0], lux);
  const fixPatch = alt ? { companies: [alt] } : { companies: [] };
  const fix_fr = alt
    ? `Voir ${LUX_LABEL[alt]}, qui navigue dans ${REGION_LABEL[regions[0]].fr} →`
    : `Voir toutes les compagnies dans ${regFr} →`;
  const fix_en = alt
    ? `See ${LUX_LABEL[alt]}, which sails ${REGION_LABEL[regions[0]].en} →`
    : `See all cruise lines in ${regEn} →`;

  return {
    reason: "lux_region",
    title_fr: `${lineNames} ne ressort pas pour ${regFr}`,
    title_en: `${lineNames} doesn't come up for ${regEn}`,
    body_fr: `Dans nos départs confirmés, ${lineNames} navigue surtout en ${primFr} — on n'a pas d'itinéraire confirmé en ${regFr}. Vous risquez de ne trouver aucune option ici.`,
    body_en: `In our confirmed departures, ${lineNames} mostly sails ${primEn} — we have no confirmed itinerary in ${regEn}. You may not find any option here.`,
    fix_fr, fix_en, fixPatch,
  };
}
